/**
 * Remove unrecorded measurements from a finished rating narrative.
 *
 * The narrative templates interpolate every field of a region, so an exam that
 * recorded only some motions printed lines like "CMC radial abduction nullº = 0%"
 * or "Varus Deformity null° = 0%" into the report the doctor pastes. An
 * unrecorded field already contributes 0% to every total, so dropping its text
 * never changes a number; it only stops the report claiming a measurement.
 *
 *  - "- DIP flexion nullº = 0%, extension 30º = 10%; added = 10% [...]"
 *        -> "- DIP extension 30º = 10% [...]"        (keep the recorded half)
 *  - "- Varus Deformity null° = 0% ..."               -> line removed
 *  - "- Ulnar (...): null mm → ..."                   -> line removed
 *  - "...; null% digit length [...]"                  -> "; digit length not recorded [...]"
 */
const UNRECORDED = /\bnull\b|\bundefined\b|\bNaN\b/;

export function omitUnrecorded(text: string): string {
  return text
    .split('\n')
    .flatMap((line) => {
      if (!UNRECORDED.test(line)) return [line];
      let l = line;
      const before = l;
      // Two-clause joint lines: drop whichever half was not recorded.
      l = l.replace(/^(- (?:DIP|PIP|MCP|IP) )flexion (?:null|undefined|NaN)[º°] = [\d.]+%, extension /, '$1extension ');
      l = l.replace(/, extension (?:null|undefined|NaN)[º°] = [\d.]+%/, '');
      // With one half gone, "added = X%" just repeats that half.
      if (l !== before) l = l.replace(/; added = [\d.]+%/, '');
      // A recorded two-point value whose digit-length percent was not recorded.
      l = l.replace(/(?:null|undefined|NaN)% digit length/, 'digit length not recorded');
      // Anything still naming an unrecorded value is a line about nothing.
      if (UNRECORDED.test(l)) return [];
      return [l];
    })
    .join('\n');
}
